import { useMemo } from 'react';

import Duration from '@/core/duration';
import { RATE_UNAVAILABLE_LABEL, formatCredits, roundOffCredits } from '@/utils';

import { Select } from '../FormAdapters';

// Note: This component uses `any` for props to match the react-final-form Field pattern
// where input/meta props are injected. The underlying Select component from FormAdapters
// also uses loose typing to pass props through to the underlying CustomSelect.
export default function DurationPicker({
  showCredits,
  showDollars,
  creditRate,
  hourlyRate,
  ...other
}: any): JSX.Element {
  const options = useMemo(
    () =>
      [30, 45, 60].map((d) => {
        let priceText = '';

        if (showDollars) {
          // Nullish, not falsy — see the credits branch below. A null hourlyRate means
          // the API could not price the expert; substituting a rate or dropping the
          // price both mislead the customer about what they are agreeing to.
          if (hourlyRate === null || hourlyRate === undefined) {
            priceText = ` (${RATE_UNAVAILABLE_LABEL})`;
          } else {
            // hourlyRate is in cents, calculate for duration
            const totalCents = (hourlyRate * d) / 60;
            const dollars = Math.round(totalCents / 100);
            priceText = ` ($${dollars})`;
          }
        } else if (showCredits) {
          // Nullish, not falsy: the API returns null when it cannot price the
          // expert, and 0 is a legitimate rate. A truthiness check treated both
          // the same and silently dropped the price from every option, leaving
          // the user to pick a duration with no price shown at all (ENG-5283/5571).
          if (creditRate === null || creditRate === undefined) {
            priceText = ` (${RATE_UNAVAILABLE_LABEL})`;
          } else {
            // same rule used to create transactions
            const creditCents = (creditRate * d) / 60;
            const credits = roundOffCredits(creditCents);

            const creditsLabel = credits / 100 === 1 ? 'credit' : 'credits';
            priceText = ` (${formatCredits(credits)} ${creditsLabel})`;
          }
        }

        return {
          value: Duration.parse(`${d}m`).toString(),
          label: `${d} minutes${priceText}`,
        };
      }),
    [showCredits, showDollars, creditRate, hourlyRate]
  );

  return <Select label="How long do you expect the call to last?" options={options} {...other} />;
}
